/**
 * Publisher-weighted forward forecast at the sector level — pure utility module.
 *
 * Mirrors the per-stock {@link computeForecast} (forecastUtils.ts) but operates
 * over a list of {@link AlphaResult} (sector articles) instead of a list of
 * already-flattened {@link StockArticleDto}. For each sector article, every
 * embedded ticker contributes to each horizon weighted by
 *
 *   (conviction / 100) · n_factor · age_decay
 *
 * where {@code n_factor = sqrt(sampleCount_T)} (or {@code lowSampleFloor} when
 * the publisher has only one sample at this horizon) and {@code age_decay} is
 * 0.5 raised to {@code ageDays / halfLifeDays}.
 *
 * The result is a list of 10 forecast points — one per {@code PriceTimeframe}
 * — each carrying:
 *   - expectedPct: weighted-mean expected % return at this horizon
 *   - sigmaPct:    floored weighted-stddev across contributions
 *   - upperPct/lowerPct: ±1σ band edges
 *   - contributingArticles: how many (article × ticker) pairs voted
 *
 * No price anchoring (sectors don't have a "price") — the consumer renders
 * these as percentages departing from 0% at "now".
 */
import { AlphaResult, PublisherMetrics } from '../../api';

// ─── Public types ────────────────────────────────────────────────────────────

export interface SectorForecastPoint {
    label: string;
    offsetMs: number;
    expectedPct: number;
    sigmaPct: number;
    upperPct: number;
    lowerPct: number;
    /** Number of (article × ticker) pairs whose weight contributed (> 0). */
    contributingArticles: number;
}

export interface SectorForecastResult {
    /** Always length 10, in TIMEFRAMES order. Empty only when there are zero articles. */
    points: SectorForecastPoint[];
    /** True iff any horizon produced Σ weight > 0. */
    hasSignal: boolean;
    /** Why hasSignal=false. Absent when hasSignal=true. */
    reason?: string;
}

// ─── Timeframes (must match PriceTimeframe ordering on the backend) ──────────

export const SECTOR_TIMEFRAMES: ReadonlyArray<{ label: string; offsetMs: number }> = [
    { label: '1h', offsetMs: 3.6e6 },
    { label: '1d', offsetMs: 8.64e7 },
    { label: '1w', offsetMs: 6.048e8 },
    { label: '2w', offsetMs: 1.2096e9 },
    { label: '4w', offsetMs: 2.4192e9 },
    { label: '2m', offsetMs: 5.184e9 },
    { label: '3m', offsetMs: 7.776e9 },
    { label: '4m', offsetMs: 1.0368e10 },
    { label: '5m', offsetMs: 1.296e10 },
    { label: '6m', offsetMs: 1.5552e10 },
];

const SCORE_KEYS: Array<keyof PublisherMetrics> = [
    'score1h', 'score1d', 'score1w', 'score2w', 'score4w',
    'score2m', 'score3m', 'score4m', 'score5m', 'score6m',
];
const COUNT_KEYS: Array<keyof PublisherMetrics> = [
    'sampleCount1h', 'sampleCount1d', 'sampleCount1w', 'sampleCount2w', 'sampleCount4w',
    'sampleCount2m', 'sampleCount3m', 'sampleCount4m', 'sampleCount5m', 'sampleCount6m',
];

// ─── Options ─────────────────────────────────────────────────────────────────

export interface ComputeSectorForecastOptions {
    nowMs?: number;
    halfLifeDays?: number;
    lowSampleFloor?: number;
    sigmaFloorPct?: number;
}

// ─── Implementation ──────────────────────────────────────────────────────────

interface Contribution {
    expectedPct: number;
    weight: number;
}

function buildContribution(
    article: AlphaResult,
    tickerStance: number,
    tickerConviction: number,
    score: number | undefined,
    sampleCount: number | undefined,
    nowMs: number,
    halfLifeDays: number,
    lowSampleFloor: number,
): Contribution | null {
    if (score == null || !Number.isFinite(score)) return null;
    if (sampleCount == null || sampleCount <= 0) return null;
    if (!Number.isFinite(tickerStance) || tickerStance === 0) return null;
    if (!Number.isFinite(tickerConviction) || tickerConviction <= 0) return null;
    if (!article.publishedAt) return null;
    const publishedMs = Date.parse(article.publishedAt);
    if (!Number.isFinite(publishedMs)) return null;

    const expectedPct = score * (tickerStance / 100);
    const ageDays = Math.max(0, (nowMs - publishedMs) / 86_400_000);
    const ageDecay = Math.pow(0.5, ageDays / halfLifeDays);
    const nFactor = sampleCount < 2 ? lowSampleFloor : Math.sqrt(sampleCount);
    const weight = (tickerConviction / 100) * nFactor * ageDecay;
    if (!Number.isFinite(weight) || weight <= 0) return null;
    return { expectedPct, weight };
}

export function computeSectorForecast(
    articles: AlphaResult[],
    metricsByAuthor: Record<string, PublisherMetrics>,
    options: ComputeSectorForecastOptions = {},
): SectorForecastResult {
    const nowMs          = options.nowMs          ?? Date.now();
    const halfLifeDays   = options.halfLifeDays   ?? 14;
    const lowSampleFloor = options.lowSampleFloor ?? 0.25;
    const sigmaFloorPct  = options.sigmaFloorPct  ?? 0.25;

    if (!articles || articles.length === 0) {
        return { points: [], hasSignal: false, reason: 'no articles in window' };
    }

    const hasAnyScoredPublisher = articles.some(a => {
        const m = a.authorId ? metricsByAuthor[a.authorId] : undefined;
        if (!m) return false;
        return COUNT_KEYS.some(k => {
            const n = m[k] as number | undefined;
            return typeof n === 'number' && n > 0;
        });
    });

    const points: SectorForecastPoint[] = [];
    let anyHorizonHadSignal = false;

    for (let i = 0; i < SECTOR_TIMEFRAMES.length; i++) {
        const tf = SECTOR_TIMEFRAMES[i];
        const contributions: Contribution[] = [];

        for (const a of articles) {
            const m = a.authorId ? metricsByAuthor[a.authorId] : undefined;
            if (!m) continue;
            const score = m[SCORE_KEYS[i]] as number | undefined;
            const sampleCount = m[COUNT_KEYS[i]] as number | undefined;
            if (score == null || sampleCount == null || sampleCount <= 0) continue;

            const tickers = a.tickers ?? [];
            if (tickers.length > 0) {
                for (const t of tickers) {
                    const c = buildContribution(
                        a,
                        Number(t.stance ?? 0),
                        Number(t.conviction ?? 0),
                        score,
                        sampleCount,
                        nowMs,
                        halfLifeDays,
                        lowSampleFloor,
                    );
                    if (c) contributions.push(c);
                }
            } else {
                // Fallback: production AlphaResult rows don't always populate
                // per-ticker stance/conviction — only the article-level
                // sectorOutlook field. Treat that as the article's "stance"
                // with implied conviction=100 so the article still contributes
                // to the forecast rather than silently dropping the signal.
                const c = buildContribution(
                    a,
                    Number(a.sectorOutlook ?? 0),
                    100,
                    score,
                    sampleCount,
                    nowMs,
                    halfLifeDays,
                    lowSampleFloor,
                );
                if (c) contributions.push(c);
            }
        }

        const totalWeight = contributions.reduce((s, c) => s + c.weight, 0);

        let expectedPct = 0;
        let variance    = 0;
        if (totalWeight > 0) {
            anyHorizonHadSignal = true;
            expectedPct = contributions.reduce((s, c) => s + c.expectedPct * c.weight, 0) / totalWeight;
            variance    = contributions.reduce((s, c) => {
                const d = c.expectedPct - expectedPct;
                return s + d * d * c.weight;
            }, 0) / totalWeight;
        }

        const sigmaRaw = Math.sqrt(Math.max(0, variance));
        const sigmaPct = Math.max(sigmaRaw, sigmaFloorPct);

        points.push({
            label:                tf.label,
            offsetMs:             tf.offsetMs,
            expectedPct,
            sigmaPct,
            upperPct:             expectedPct + sigmaPct,
            lowerPct:             expectedPct - sigmaPct,
            contributingArticles: contributions.length,
        });
    }

    if (!hasAnyScoredPublisher) {
        return { points, hasSignal: false, reason: 'no scored publishers' };
    }
    return {
        points,
        hasSignal: anyHorizonHadSignal,
        ...(anyHorizonHadSignal ? {} : { reason: 'no net signal across any horizon' }),
    };
}

// ─── Historical line ─────────────────────────────────────────────────────────

export interface SectorOutlookPoint {
    /** yyyy-MM-dd. */
    date: string;
    /** Conviction-weighted average ticker stance across all sector articles on this day. -100..100. */
    value: number;
    /** Total number of (article × ticker) pairs aggregated. Useful for tooltips. */
    sampleCount: number;
}

/**
 * Conviction-weighted aggregate sector outlook per day.
 *
 * For each calendar day in {@code articles}, walks every embedded ticker in
 * every article published on that day and produces a Σ(stance · conviction) /
 * Σ conviction average. Days with zero conviction emit no point — the
 * chart simply skips that day rather than rendering a misleading 0.
 *
 * Output is sorted ascending by date for direct LineChart consumption.
 */
export function buildSectorOutlookSeries(articles: AlphaResult[]): SectorOutlookPoint[] {
    const byDay = new Map<string, { sw: number; sc: number; n: number }>();
    for (const a of articles ?? []) {
        const day = (a.publishedAt ?? '').substring(0, 10);
        if (!day) continue;
        const tickers = a.tickers ?? [];
        if (tickers.length > 0) {
            for (const t of tickers) {
                const stance     = Number(t.stance ?? 0);
                const conviction = Number(t.conviction ?? 0);
                if (!Number.isFinite(stance) || !Number.isFinite(conviction)) continue;
                if (conviction <= 0) continue;
                const current = byDay.get(day) ?? { sw: 0, sc: 0, n: 0 };
                current.sw += stance * conviction;
                current.sc += conviction;
                current.n  += 1;
                byDay.set(day, current);
            }
        } else {
            // Fallback: production AlphaAnalysisResults rows often have
            // tickers=[]. Use the article-level sectorOutlook with implied
            // conviction=100 so we still draw a sensible aggregate line
            // rather than an empty chart.
            const outlook = Number(a.sectorOutlook ?? 0);
            if (!Number.isFinite(outlook)) continue;
            const current = byDay.get(day) ?? { sw: 0, sc: 0, n: 0 };
            current.sw += outlook * 100;
            current.sc += 100;
            current.n  += 1;
            byDay.set(day, current);
        }
    }
    return [...byDay.entries()]
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([date, { sw, sc, n }]) => ({
            date,
            value: sc > 0 ? sw / sc : 0,
            sampleCount: n,
        }));
}
